/**
 * Created by piotr.pozniak@thebeaverhead.com on 19/10/2018.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import AuthLayout from "./AuthPages/AuthLayout";
import WhatsNewPane from "./AuthPages/WhatsNewPane";
import AuthStateCard from "./AuthPages/AuthStateCard";
import AuthAlert from "./AuthPages/AuthAlert";
import { Input, Button } from "../theme/untitled";
import { useAppStore } from "./../../../hooks/redux/app";
import { store } from "../store";
import { useUserStore } from "../../../hooks/redux/user";
import { formatError } from "../../../helpers/errors";
import styles from "./AuthPages/authForm.module.scss";

const MIN_PASSWORD_LENGTH = 6;

const PasswordResetPage = (props) => {
  const email = decodeURIComponent(props.match.params.email);
  const token = props.match.params.token;

  const { user, resetPassword } = useUserStore();
  const history = useHistory();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState(null);
  const [confirmError, setConfirmError] = useState(null);
  const { clearErrors } = useAppStore();

  useEffect(() => {
    return clearErrors;
  }, []);

  const isInvalidToken = user.resetPasswordError?.response?.status === 404;
  const genericError = isInvalidToken ? null : user.resetPasswordError;

  const genericErrorMessage = useMemo(() => {
    if (!genericError) {
      return null;
    }
    return formatError(genericError, "Could not reset your password. Please try again.").join(" ");
  }, [genericError]);

  const onSubmit = async (e) => {
    e.preventDefault();

    let nextPasswordError = null;
    let nextConfirmError = null;

    if (!password || password.length < MIN_PASSWORD_LENGTH) {
      nextPasswordError = `Please enter a password, at least ${MIN_PASSWORD_LENGTH} characters.`;
    } else if (password !== confirmPassword) {
      nextConfirmError = "Both passwords must match.";
    }

    setPasswordError(nextPasswordError);
    setConfirmError(nextConfirmError);

    if (nextPasswordError || nextConfirmError) {
      return;
    }

    await resetPassword(email, token, password);

    if (store.getState().user.resetPasswordSuccess) {
      clearErrors();
      history.push("/dashboard");
    }
  };

  const isLoading = user.resetPassword;

  if (isInvalidToken) {
    return (
      <AuthLayout rightPane={<WhatsNewPane />}>
        <AuthStateCard
          tone="error"
          icon="AlertCircle"
          title="This link has expired"
          actions={
            <>
              <Button hierarchy="primary" onClick={() => history.push("/password-reminder")}>
                Send a new link
              </Button>
              <Button hierarchy="secondaryGray" onClick={() => history.push("/login")}>
                Back to log in
              </Button>
            </>
          }
        >
          <p>Reset links last 60 minutes. Ask for a fresh one and we'll email it straight away.</p>
        </AuthStateCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout rightPane={<WhatsNewPane />}>
      <form action="/" noValidate>
        <h1 className={styles.title}>Set a new password</h1>
        <p className={styles.sub}>
          For <b>{email}</b>.
        </p>

        <div className={styles.fields}>
          <Input
            label="New password"
            type="password"
            placeholder=""
            value={password}
            autoFocus
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            error={passwordError}
            autoComplete="off"
          />

          <Input
            label="Confirm new password"
            type="password"
            placeholder=""
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isLoading}
            error={confirmError}
            autoComplete="off"
          />

          {genericErrorMessage && <AuthAlert variant="error">{genericErrorMessage}</AuthAlert>}

          <Button type="submit" fullWidth loading={isLoading} onClick={onSubmit}>
            Save and log in
          </Button>
        </div>

        <p className={styles.footLink}>
          <Link to="/login">Back to log in</Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default PasswordResetPage;
